import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Sparkle, CalendarBlank, Trophy, Star, Heart, ArrowRight, SpinnerGap,
} from "@phosphor-icons/react";

const BASE = "";

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("pt-PT", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return s; }
}

export default function DreamsSharePublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BASE}/api/dreams/public/share/${token}`);
        if (!res.ok) throw new Error("Link inválido ou revogado.");
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) return (
    <div className="dreams-root min-h-screen bg-[#FAF8F3] flex items-center justify-center">
      <SpinnerGap size={28} className="animate-spin text-[#6F4F1D]" />
    </div>
  );

  if (err) return (
    <div className="dreams-root min-h-screen bg-[#FAF8F3] text-[#0F1829] flex flex-col items-center justify-center px-6 text-center">
      <Sparkle size={36} weight="fill" className="text-[#6F4F1D]/60" />
      <h1 className="text-3xl font-bold mt-4">Este Dream Board já não está disponível</h1>
      <p className="text-[#978D78] mt-2 max-w-md">O link pode ter sido revogado ou nunca existiu.</p>
      <Link to="/dreams" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#6F4F1D] text-[#0F1829] font-semibold text-sm">
        Cria o teu Dream Board <ArrowRight size={14} weight="bold" />
      </Link>
    </div>
  );

  const dream = data?.next_dream;
  const sonhador = data?.sonhador || "Sonhador";

  return (
    <div className="dreams-root min-h-screen bg-[#FAF8F3] text-[#0F1829] relative overflow-hidden">
      {/* Aurora background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-32 w-[600px] h-[600px] rounded-full bg-[#0F1829]/25 blur-3xl" />
        <div className="absolute top-1/3 -right-40 w-[500px] h-[500px] rounded-full bg-[#978D78]/20 blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] rounded-full bg-[#6F4F1D]/15 blur-3xl" />
      </div>

      {/* Top brand */}
      <header className="relative z-10 max-w-3xl mx-auto pt-8 px-6 flex items-center justify-between">
        <Link to="/dreams" className="flex items-center gap-2 group" data-testid="share-logo">
          <Sparkle size={20} weight="fill" className="text-[#6F4F1D] group-hover:rotate-12 transition-transform" />
          <span className="text-base font-semibold tracking-tight">IHTP <span className="text-[#6F4F1D]">Dreams</span></span>
        </Link>
        <span className="text-[10px] uppercase tracking-widest text-[#978D78]">Dream Board partilhado</span>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-6 py-12">
        <p className="text-xs uppercase tracking-widest text-[#6F4F1D]/80 mb-3">O sonho de</p>
        <h1 className="text-5xl sm:text-6xl font-bold tracking-tight leading-[1.1]" data-testid="share-sonhador">
          {sonhador}
        </h1>
        <p className="text-[#4B5563] mt-3 max-w-xl">
          Apoia esta jornada — cada sonho concretizado começa com alguém que acredita.
        </p>

        {dream ? (
          <section className="mt-10 rounded-3xl border border-[#E6E1D6] bg-gradient-to-br from-violet-900/30 via-fuchsia-900/15 to-amber-900/10 overflow-hidden">
            <div className="relative aspect-[16/9] bg-gradient-to-br from-amber-500/30 to-fuchsia-700/30">
              {dream.FotoSonhoUrl ? (
                <img src={dream.FotoSonhoUrl} alt={dream.Titulo} className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Sparkle size={64} weight="fill" className="text-[#6F4F1D]/40" />
                </div>
              )}
              <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-[#6F4F1D] text-[#0F1829] text-[10px] font-bold uppercase tracking-wider">
                Próximo sonho
              </span>
            </div>
            <div className="p-6 sm:p-10">
              <h2 className="text-3xl sm:text-4xl font-semibold leading-tight" data-testid="share-dream-title">{dream.Titulo}</h2>
              {dream.Descricao && (
                <p className="text-[#0F1829] mt-4 leading-relaxed">{dream.Descricao}</p>
              )}
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <Stat icon={CalendarBlank} label="Alvo" value={fmtDate(dream.DataAlvo)} />
                {dream.Valor != null && (
                  <Stat icon={Star} label="Valor" value={`${Number(dream.Valor).toLocaleString("pt-PT")}€`} />
                )}
                {dream.Premio && (
                  <Stat icon={Trophy} label="Prémio" value={dream.Premio} fullWidth />
                )}
              </div>
            </div>
          </section>
        ) : (
          <section className="mt-10 rounded-3xl border border-dashed border-[#E6E1D6] p-12 text-center">
            <Sparkle size={32} weight="fill" className="text-[#6F4F1D]/60 mx-auto" />
            <p className="text-[#4B5563] mt-3">Ainda não há sonho activo neste Dream Board.</p>
          </section>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-2 text-xs text-[#978D78]">
          <span className="px-3 py-1 rounded-full bg-white border border-[#E6E1D6]">
            <Heart size={11} weight="fill" className="text-rose-400 inline mr-1" />
            {data?.counts?.abertos || 0} sonhos activos · {data?.counts?.concluidos || 0} concretizados
          </span>
        </div>

        <section className="mt-12 rounded-2xl border border-[#6F4F1D]/25 bg-[#6F4F1D]/5 p-6 text-center">
          <h3 className="text-xl font-semibold">Também tens um sonho?</h3>
          <p className="text-sm text-[#4B5563] mt-2 max-w-lg mx-auto">
            O IHTP Dreams transforma a tua visão de vida ideal em sonhos concretos, com IA a sugerir-te o plano de acção.
          </p>
          <Link to={data?.refer_id ? `/dreams/landing?REFER=${data.refer_id}` : "/dreams/landing"}
                data-testid="share-cta-join"
                className="inline-flex items-center gap-2 mt-5 px-6 py-2.5 rounded-full bg-gradient-to-r from-[#6F4F1D] to-fuchsia-500 text-[#0F1829] font-semibold text-sm">
            Começar o meu Dream Board <ArrowRight size={14} weight="bold" />
          </Link>
        </section>
      </main>
    </div>
  );
}

function Stat({ icon: Icon, label, value, fullWidth }) {
  return (
    <div className={`flex items-start gap-3 ${fullWidth ? "sm:col-span-2" : ""}`}>
      <div className="w-9 h-9 rounded-lg bg-[#6F4F1D]/10 border border-[#6F4F1D]/25 flex items-center justify-center shrink-0">
        <Icon size={16} weight="fill" className="text-[#6F4F1D]" />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-widest text-[#6F4F1D]/80">{label}</p>
        <p className="text-sm text-white mt-0.5">{value}</p>
      </div>
    </div>
  );
}
